import AsyncStorage from '@react-native-async-storage/async-storage';


const LOGGLY_CONFIG = {
  token: "54f02086-c89b-45bb-8ba6-bf702deb973d", // Same token as GroovePacker
  subdomain: "groovepacker", // GroovePacker subdomain
  tags: ["GPX", "Mobile", "PrintOperations", "GroovePacker"],
  endpoint: `https://logs-01.loggly.com/inputs/54f02086-c89b-45bb-8ba6-bf702deb973d/tag/GPX,Mobile,PrintOperations,GroovePacker/`
};

// Session tracking for print operations
let currentSession = null;
let sessionStartTime = null;
export const startPrintSession = (sessionType = 'print_operation') => {
  currentSession = `GPX_${sessionType}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  sessionStartTime = new Date().toISOString();



  // Log session start
  sendToLoggly({
    timestamp: sessionStartTime,
    type: 'SESSION_START',
    log_type: 'GPX_PRINTING',
    session_id: currentSession,
    session_type: sessionType,
    message: `GPX print session started: ${sessionType}`,
    app: 'GPX_Mobile',
    source: 'GPX',
    platform: 'Mobile',
    category: 'session',
    level: 'info'
  });

  return currentSession;
};

/**
 * End current print session
 */
export const endPrintSession = (status = 'completed') => {
  if (currentSession) {
    const endTime = new Date().toISOString();
    const duration = sessionStartTime ?
      new Date(endTime).getTime() - new Date(sessionStartTime).getTime() : 0;



    // Log session end
    sendToLoggly({
      timestamp: endTime,
      type: 'SESSION_END',
      log_type: 'GPX_PRINTING',
      session_id: currentSession,
      session_status: status,
      session_duration_ms: duration,
      message: `GPX print session ended: ${status}`,
      app: 'GPX_Mobile',
      source: 'GPX',
      platform: 'Mobile',
      category: 'session',
      level: status === 'completed' ? 'info' : 'warn'
    });

    currentSession = null;
    sessionStartTime = null;
  }
};

// Initialize logger (call this in App.js)
export const initializeLogger = async () => {
  try {
    // Check if Loggly logging is enabled
    const logglyEnabled = await isLogglyEnabled();

    // Get user info for identification
    const userInfo = await AsyncStorage.getItem('userInfo');
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;

    console.log(`GPX Frontend Logger initialized - Loggly: ${logglyEnabled ? 'ENABLED' : 'DISABLED'}`);

    // Send initialization log to Loggly (only if enabled)
    if (logglyEnabled) {
      await sendToLoggly({
        timestamp: new Date().toISOString(),
        type: 'LOGGER_INIT',
        log_type: 'GPX_SYSTEM',
        message: 'GPX Mobile frontend logger initialized with Loggly enabled',
        user: parsedUserInfo?.name,
        tenant: parsedUserInfo?.current_tenant,
        app: 'GPX_Mobile',
        source: 'GPX',
        platform: 'Mobile',
        category: 'system'
      });
    }

  } catch (error) {
    console.error('Failed to initialize logger:', error);
  }
};

/**
 * Check if Loggly logging is enabled
 */
const isLogglyEnabled = async () => {
  try {
    const logglyEnabled = await AsyncStorage.getItem('loggly_gpx_order_scan');
    return logglyEnabled === 'true';
  } catch (error) {
    console.error('Failed to check Loggly setting:', error);
    return false; // Default to disabled if can't read setting
  }
};

/**
 * Send log to Loggly service using HTTP POST (only if enabled)
 */
const sendToLoggly = async (logData) => {
  try {
    // Check if Loggly logging is enabled
    const logglyEnabled = await isLogglyEnabled();
    if (!logglyEnabled) {
      console.log('Loggly logging disabled - skipping log');
      return;
    }

    // Add timestamp if not present
    if (!logData.timestamp) {
      logData.timestamp = new Date().toISOString();
    }

    // Send to Loggly via HTTP POST
    const response = await fetch(LOGGLY_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logData)
    });

    if (!response.ok) {
      console.warn('Loggly HTTP response not OK:', response.status, response.statusText);
    }

  } catch (error) {
    console.error('Loggly failed to send log:', error);
    // Don't throw - logging failures shouldn't break the app
  }
};

/**
 * Log print operations with full context - sends to Loggly dashboard
 *
 * 🔍 SEARCH IN LOGGLY:
 * - All print operations: log_type:GPX_PRINTING AND type:PRINT_OPERATION
 * - Successful prints: log_type:GPX_PRINTING AND status:success
 * - Failed prints: log_type:GPX_PRINTING AND status:failed
 * - Specific order: order_id:"ORDER_NUMBER"
 * - Specific print type: print_type:"Shipping Label"
 */
export const logPrintOperation = async (printType, printData, status = 'success', error = null) => {
  try {
    const userInfo = await AsyncStorage.getItem('userInfo');
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
    const timestamp = new Date().toISOString();



    const orderId = printData.current_increment_id ||
                   printData.increment_id ||
                   printData.orderId ||
                   printData.order_id ||
                   printData.order?.current_increment_id ||
                   printData.order?.increment_id ||
                   printData.order?.id ||
                   printData.msgData?.current_increment_id ||
                   printData.msgData?.increment_id ||
                   printData.msgData?.orderId ||
                   printData.print_type ||
                   'No_Order_ID_Available';



    const logEntry = {
      timestamp,
      type: 'PRINT_OPERATION',
      log_type: 'GPX_PRINTING',
      print_type: printType,
      status: status,
      order_id: orderId,
      increment_id: printData.increment_id,
      url: printData.url,
      printer: printData.printer,
      dimensions: printData.dimensions,
      user: parsedUserInfo?.name,
      tenant: parsedUserInfo?.current_tenant,
      app: 'GPX_Mobile',
      source: 'GPX',                      // 🔍 Search: source:GPX
      platform: 'Mobile',
      category: 'printing',               // 🔍 Search: category:printing
      operation: 'print_label',           // 🔍 Search: operation:print_label
      session_id: currentSession,         // 🔍 Search: session_id:"SESSION_ID"
      level: status === 'success' ? 'info' : 'error',
      // Enhanced search tags for easier filtering
      search_tags: ['gpx_print', printType.toLowerCase().replace(/\s+/g, '_'), status],
      feature: 'PRINTING'                 // 🔍 Search: feature:PRINTING
    };

    if (status === 'success') {
      const displayOrderId = orderId !== 'No_Order_ID_Available' ? orderId : 'Unknown Order';

      logEntry.message = `✅ GPX PRINT SUCCESS: ${printType} printed successfully for Order #${displayOrderId}`;
      logEntry.result = 'SUCCESS';
      logEntry.order_display_id = displayOrderId;
      logEntry.success_summary = `${printType} completed for Order #${displayOrderId}`;
      logEntry.success_details = {
        order_number: displayOrderId,
        printer_used: printData.printer,
        label_dimensions: printData.dimensions,
        print_url: printData.url,
        print_type: printType,
        user_name: parsedUserInfo?.name,
        tenant_name: parsedUserInfo?.current_tenant
      };

    } else {
      const displayOrderId = orderId !== 'No_Order_ID_Available' ? orderId : 'Unknown Order';
      const fullErrorMessage = error?.message || printData.error || 'Print operation failed';
      const errorStack = error?.stack || 'No stack trace available';
      const errorType = error?.name || error?.constructor?.name || 'PrintError';

      logEntry.message = `❌ GPX PRINT FAILED: ${printType} failed for Order #${displayOrderId} - ${fullErrorMessage}`;
      logEntry.result = 'FAILED';
      logEntry.order_display_id = displayOrderId;
      logEntry.error_message = fullErrorMessage;
      logEntry.error_type = errorType;
      logEntry.error_stack = errorStack;
      logEntry.failure_reason = fullErrorMessage;

      logEntry.failure_summary = `${printType} failed for Order #${displayOrderId}: ${fullErrorMessage}`;
      logEntry.full_error_details = {
        order_number: displayOrderId,
        print_type: printType,
        printer_attempted: printData.printer,
        label_url: printData.url,
        print_dimensions: printData.dimensions,
        error_type: errorType,
        error_message: fullErrorMessage,
        error_stack: errorStack,
        order_increment_id: printData.increment_id,
        order_id: printData.orderId,
        timestamp_error: new Date().toISOString(),
        user_name: parsedUserInfo?.name,
        tenant_name: parsedUserInfo?.current_tenant,
        browser_info: navigator?.userAgent || 'Unknown'
      };

      logEntry.ERROR_SUMMARY = `❌ PRINT FAILED: ${printType} | Order #${displayOrderId} | ${fullErrorMessage}`;
      logEntry.TROUBLESHOOT_INFO = `🔧 Check: Printer "${printData.printer}" | QZ Tray connection | Label URL access`;
      logEntry.QUICK_FIX_HINTS = `💡 Try: Restart QZ Tray | Check printer connection | Verify label URL: ${printData.url?.substring(0, 50)}...`;
    }

    await sendToLoggly(logEntry);

  } catch (logError) {
    console.error('Failed to log print operation:', logError);
  }
};

export const logQzTrayConnection = async (connectionData, success = true, error = null) => {
  try {
    const userInfo = await AsyncStorage.getItem('userInfo');
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;



    const orderId = connectionData.order_data?.current_increment_id ||
                   connectionData.order_data?.increment_id ||
                   connectionData.order_data?.orderId ||
                   connectionData.order_data?.order_id ||
                   connectionData.orderId ||
                   connectionData.order_id ||
                   connectionData.increment_id ||
                   connectionData.current_increment_id ||
                   connectionData.order ||
                   'No_Order_ID_Available';



    const timestamp = new Date().toISOString();

    const logEntry = {
      timestamp,
      type: 'QZ_TRAY_CONNECTION',
      log_type: 'GPX_PRINTING',
      status: success ? 'success' : 'failed',
      host: connectionData.host || 'localhost',
      port: connectionData.port || 8181,
      order_id: orderId,
      user: parsedUserInfo?.name,
      tenant: parsedUserInfo?.current_tenant,
      app: 'GPX_Mobile',
      source: 'GPX',
      platform: 'Mobile',
      category: 'qz_tray',
      level: success ? 'info' : 'error',
      session_id: currentSession,
      feature: 'QZ_TRAY',
      search_tags: ['qz_tray', 'connection', success ? 'success' : 'failed']
    };

    if (success) {
      const displayOrderId = orderId !== 'No_Order_ID_Available' ? orderId : 'Unknown Order';

      logEntry.message = `✅ GPX QZ TRAY SUCCESS: Connected to ${connectionData.host}:${connectionData.port} for Order #${displayOrderId}`;
      logEntry.result = 'SUCCESS';
      logEntry.order_display_id = displayOrderId;
      logEntry.connection_summary = `QZ Tray connected for Order #${displayOrderId}`;
      logEntry.connection_details = {
        order_number: displayOrderId,
        host: connectionData.host,
        port: connectionData.port,
        secure: connectionData.secure,
        address: connectionData.address,
        user_name: parsedUserInfo?.name,
        tenant_name: parsedUserInfo?.current_tenant
      };

    } else {
      const displayOrderId = orderId !== 'No_Order_ID_Available' ? orderId : 'Unknown Order';
      const fullErrorMessage = error?.message || 'QZ Tray connection failed';
      const errorStack = error?.stack || 'No stack trace available';
      const errorType = error?.name || error?.constructor?.name || 'QzTrayConnectionError';

      logEntry.message = `❌ GPX QZ TRAY FAILED: Connection failed for Order #${displayOrderId} - ${fullErrorMessage}`;
      logEntry.result = 'FAILED';
      logEntry.order_display_id = displayOrderId;
      logEntry.error_message = fullErrorMessage;
      logEntry.error_type = errorType;
      logEntry.failure_reason = fullErrorMessage;

      logEntry.failure_summary = `QZ Tray connection failed for Order #${displayOrderId}: ${fullErrorMessage}`;
      logEntry.full_qz_error_details = {
        order_number: displayOrderId,
        host: connectionData.host || 'localhost',
        port: connectionData.port || 8181,
        secure: connectionData.secure,
        address: connectionData.address,
        error_type: errorType,
        error_message: fullErrorMessage,
        error_stack: errorStack,
        connection_attempt_time: timestamp,
        user_name: parsedUserInfo?.name,
        tenant_name: parsedUserInfo?.current_tenant,
        browser_info: navigator?.userAgent || 'Unknown'
      };

      logEntry.QZ_ERROR_SUMMARY = `❌ QZ TRAY FAILED: Order #${displayOrderId} | ${fullErrorMessage} | ${connectionData.host}:${connectionData.port}`;
      logEntry.QZ_TROUBLESHOOT_INFO = `🔧 Check: QZ Tray running on ${connectionData.host}:${connectionData.port} | WebSocket connection | Firewall settings`;
      logEntry.QZ_QUICK_FIX_HINTS = `💡 Try: Restart QZ Tray | Check port ${connectionData.port} | Verify WebSocket support`;
    }

    await sendToLoggly(logEntry);

  } catch (logError) {
    console.error('Failed to log QZ Tray connection:', logError);
  }
};

export const logWebSocketConnection = async (socketId, connectionData = {}, success = true) => {
  try {
    const timestamp = new Date().toISOString();

    const logEntry = {
      timestamp,
      type: 'WEBSOCKET_CONNECTION',
      status: success ? 'connected' : 'failed',
      socket_id: socketId,
      server: connectionData.socket_server,
      app: 'GPX_Mobile',
      source: 'GPX',
      platform: 'Mobile',
      level: success ? 'info' : 'error'
    };

    if (success) {
      logEntry.message = `WebSocket connected successfully`;


    } else {
      logEntry.message = `WebSocket connection failed`;

    }

    await sendToLoggly(logEntry);

  } catch (logError) {
    console.error('Failed to log WebSocket connection:', logError);
  }
};

export const logWebSocketDisconnection = async (reason, socketId = null) => {
  try {
    const timestamp = new Date().toISOString();

    const logEntry = {
      timestamp,
      type: 'WEBSOCKET_DISCONNECTION',
      status: 'disconnected',
      socket_id: socketId,
      reason: reason,
      message: `WebSocket disconnected: ${reason}`,
      app: 'GPX_Mobile',
      source: 'GPX',
      platform: 'Mobile',
      level: 'info'
    };



    await sendToLoggly(logEntry);

  } catch (logError) {
    console.error('Failed to log WebSocket disconnection:', logError);
  }
};

export const setCurrentOrder = async (orderData) => {
  try {
    await AsyncStorage.setItem('currentOrder', JSON.stringify(orderData));
  } catch (error) {
    console.error('Failed to set current order:', error);
  }
};
export const logCustomEvent = (eventName, data = {}) => {
  try {
    console.log(`Custom event: ${eventName}`, data);
  } catch (error) {
    console.error('Failed to log custom event:', error);
  }
};

export const logShippingLabelPrint = async (orderId, url, printer, dimensions, status = 'success', error = null) => {
  await logPrintOperation('Shipping Label', { orderId, url, printer, dimensions }, status, error);
};

export const logPackingSlipPrint = async (orderId, url, printer, dimensions, status = 'success', error = null) => {
  await logPrintOperation('Packing Slip', { orderId, url, printer, dimensions }, status, error);
};

export const logBarcodeLabelPrint = async (orderId, url, printer, dimensions, status = 'success', error = null) => {
  await logPrintOperation('Barcode Label', { orderId, url, printer, dimensions }, status, error);
};

export const logProductLabelPrint = async (productId, url, printer, dimensions, status = 'success', error = null) => {
  await logPrintOperation('Product Label', { orderId: productId, url, printer, dimensions }, status, error);
};

export const logPrintFailure = async (printType, error, printData) => {
  await logPrintOperation(printType, printData, 'failed', error);
};

export const logPrinterStatus = async (printerEvent, orderData = null) => {
  try {
    const userInfo = await AsyncStorage.getItem('userInfo');
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
    const timestamp = new Date().toISOString();

    const {
      printerName = 'Unknown',
      jobName = null,
      eventType = 'UNKNOWN',
      statusText = 'UNKNOWN',
      severity = 'INFO',
      statusCode = null,
      message = 'Printer status event'
    } = printerEvent;

    const orderIncrementId = orderData?.current_increment_id || null;

    const logEntry = {
      timestamp,
      type: 'PRINTER_STATUS',
      log_type: 'GPX_PRINTER_STATUS',
      printer_name: printerName,
      job_name: jobName,
      event_type: eventType,
      status_text: statusText,
      severity: severity.toUpperCase(),
      status_code: statusCode,
      message: message,
      user: parsedUserInfo?.name || 'Unknown',
      tenant: parsedUserInfo?.current_tenant || 'Unknown',
      app: 'GPX_Mobile',
      source: 'QZ_Tray',
      platform: 'Mobile',
      category: 'printer_status',
      level: severity.toLowerCase(),
      session_id: currentSession,
      order_increment_id: orderIncrementId,
      raw_event: printerEvent
    };

    const orderInfo = orderIncrementId ? ` | Order: ${orderIncrementId}` : '';

    if (severity === 'ERROR' || severity === 'FATAL') {
      logEntry.PRINTER_ERROR_SUMMARY = `❌ PRINTER ERROR: ${printerName} | ${statusText} | ${message}${orderInfo}`;
      logEntry.PRINTER_TROUBLESHOOT_INFO = `🔧 Check: Printer "${printerName}" | Status: ${statusText} | Event: ${eventType}${orderInfo}`;
      logEntry.PRINTER_QUICK_FIX_HINTS = `💡 Try: Check printer connection | Verify paper/toner | Restart printer | Check QZ Tray`;
    } else if (severity === 'WARN') {
      logEntry.PRINTER_WARNING_SUMMARY = `⚠️ PRINTER WARNING: ${printerName} | ${statusText} | ${message}${orderInfo}`;
      logEntry.PRINTER_ACTION_NEEDED = `🔧 Action: Check printer "${printerName}" | Status: ${statusText}${orderInfo}`;
    } else {
      logEntry.PRINTER_INFO_SUMMARY = `ℹ️ PRINTER STATUS: ${printerName} | ${statusText} | ${message}${orderInfo}`;
    }

    const consoleMessage = `[QZ PRINTER] ${severity} | ${printerName} | ${eventType} | ${statusText} | ${message}${orderInfo}`;

    switch (severity.toUpperCase()) {
      case 'ERROR':
      case 'FATAL':
        console.error(consoleMessage, printerEvent);
        break;
      case 'WARN':
        console.warn(consoleMessage, printerEvent);
        break;
      default:
        console.log(consoleMessage, printerEvent);
        break;
    }

    const severityUpper = severity.toUpperCase();
    const shouldSendToLoggly = severityUpper === 'ERROR' ||
                               severityUpper === 'FATAL' ||
                               (severityUpper === 'INFO' && (
                                 statusText.includes('SUCCESS') ||
                                 statusText.includes('COMPLETE') ||
                                 statusText.includes('PRINTED') ||
                                 eventType === 'JOB_COMPLETE'
                               ));

    if (shouldSendToLoggly) {
      await sendToLoggly(logEntry);
      console.log("Logs Sent : ", logEntry);
    }

  } catch (logError) {
    console.error('Failed to log printer status:', logError);
  }
};

export default {
  initializeLogger,
  logPrintOperation,
  logQzTrayConnection,
  logWebSocketConnection,
  logWebSocketDisconnection,
  setCurrentOrder,
  logCustomEvent,
  logShippingLabelPrint,
  logPackingSlipPrint,
  logBarcodeLabelPrint,
  logProductLabelPrint,
  logPrintFailure,
  logPrinterStatus,
  isLogglyEnabled
};